import React from "react";
import { Link } from "react-router-dom";
import {
  FaCode,
  FaShoppingCart,
  FaUsers,
  FaRobot,
  FaBrain,
  FaLayerGroup,
  FaCloud,
  FaDatabase,
  FaCogs,
  FaLightbulb,
  FaPaintBrush,
  FaProjectDiagram,
  FaArrowRight,
} from "react-icons/fa";
import "./RelatedServices.css";

const SERVICES = {
  "custom-software-development": {
    title: "Custom Software Development",
    text: "Tailor-made web and business applications built around how your company works.",
    Icon: FaCode,
  },
  "digital-commerce": {
    title: "Digital Commerce Solutions",
    text: "Online stores and commerce platforms designed to sell, scale and integrate.",
    Icon: FaShoppingCart,
  },
  "crm-platform-implementation": {
    title: "CRM Platform Implementation",
    text: "Set up and customise a CRM that keeps sales, support and marketing connected.",
    Icon: FaUsers,
  },
  "generative-ai": {
    title: "Generative AI Solutions",
    text: "Chatbots, assistants and content tools powered by generative AI.",
    Icon: FaRobot,
  },
  "machine-learning": {
    title: "Machine Learning Solutions",
    text: "Predictive models and intelligent features trained on your own data.",
    Icon: FaBrain,
  },
  "software-engineering": {
    title: "Software Engineering Services",
    text: "Reliable engineering teams for building, testing and maintaining your products.",
    Icon: FaLayerGroup,
  },
  "cloud-engineering": {
    title: "Cloud Engineering Services",
    text: "Cloud architecture, migration and deployment that scales with demand.",
    Icon: FaCloud,
  },
  "data-engineering": {
    title: "Data Engineering Services",
    text: "Data pipelines and platforms that turn raw data into usable insight.",
    Icon: FaDatabase,
  },
  "rpa-ipa-implementation": {
    title: "RPA & IPA Implementation",
    text: "Automate repetitive workflows with robotic and intelligent process automation.",
    Icon: FaCogs,
  },
  "product-strategy-consulting": {
    title: "Product Strategy & Consulting",
    text: "Turn ideas into a clear roadmap with expert product guidance.",
    Icon: FaLightbulb,
  },
  "graphics-designing": {
    title: "Graphics Designing Services",
    text: "Brand, interface and marketing visuals that look sharp everywhere.",
    Icon: FaPaintBrush,
  },
  "system-integration": {
    title: "System Integration Services",
    text: "Connect your tools and platforms so data flows between them seamlessly.",
    Icon: FaProjectDiagram,
  },
};

const RELATED = {
  "generative-ai": ["machine-learning", "data-engineering", "custom-software-development"],
  "machine-learning": ["generative-ai", "data-engineering", "cloud-engineering"],
  "data-engineering": ["machine-learning", "cloud-engineering", "system-integration"],
  "cloud-engineering": ["software-engineering", "data-engineering", "system-integration"],
  "software-engineering": ["custom-software-development", "cloud-engineering", "system-integration"],
  "custom-software-development": ["software-engineering", "digital-commerce", "product-strategy-consulting"],
  "digital-commerce": ["custom-software-development", "crm-platform-implementation", "graphics-designing"],
  "crm-platform-implementation": ["digital-commerce", "rpa-ipa-implementation", "system-integration"],
  "rpa-ipa-implementation": ["system-integration", "crm-platform-implementation", "generative-ai"],
  "product-strategy-consulting": ["custom-software-development", "software-engineering", "graphics-designing"],
  "graphics-designing": ["digital-commerce", "product-strategy-consulting", "custom-software-development"],
  "system-integration": ["cloud-engineering", "data-engineering", "rpa-ipa-implementation"],
};

// Contextual internal links shown at the bottom of each service page: three
// related services as cards, plus pointers to proof (case studies, portfolio),
// further reading (blog) and the contact page. `current` is the service
// page's own route slug (no leading slash).
function RelatedServices({ current }) {
  const related = RELATED[current];
  if (!related) return null;

  return (
    <section className="related_services_section" aria-labelledby="related-services-heading">
      <div className="related_services_container">
        <p className="related_services_eyebrow">Explore more</p>
        <h2 id="related-services-heading" className="related_services_heading">
          Related <span>Services</span>
        </h2>
        <div className="related_services_bar" />

        <ul className="related_services_grid">
          {related.map((slug) => {
            const { title, text, Icon } = SERVICES[slug];
            return (
              <li key={slug} className="related_services_item">
                <Link to={`/${slug}`} className="related_services_card">
                  <span className="related_services_icon" aria-hidden="true">
                    <Icon />
                  </span>
                  <h3 className="related_services_card_title">{title}</h3>
                  <p className="related_services_card_text">{text}</p>
                  <span className="related_services_card_link">
                    {`Learn more about ${title.toLowerCase()} `}<FaArrowRight aria-hidden="true" />
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>

        <div className="related_services_cta">
          <p className="related_services_cta_text">
            Want to see our work or talk through your project? Browse our <Link to="/case-studies">case studies</Link>, <Link to="/portfolio">portfolio</Link> or <Link to="/blog">technology articles</Link>.
          </p>
          <div className="related_services_cta_buttons">
            <Link to="/services" className="related_services_btn related_services_btn_primary">
              View all services
            </Link>
            <Link to="/contact" className="related_services_btn related_services_btn_outline">
              Contact our team
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export default RelatedServices;
